import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Smartphone,
  Wallet,
  FileText,
  Megaphone,
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Sparkles,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { device } from "../../../atoms/Utlis/media";

export interface WhatsAppUserGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GuideStep {
  id: number;
  badgeTitle: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  bgLight: string;
  actionRoute?: string;
  actionLabel?: string;
  steps: {
    title: string;
    description: string;
  }[];
  mockup: {
    header: string;
    items: string[];
    highlight?: string;
  };
  tip?: string;
}

const GUIDE_STEPS: GuideStep[] = [
  {
    id: 1,
    badgeTitle: "1. Set Up WhatsApp",
    title: "Set Up WhatsApp",
    subtitle:
      "To start using the WhatsApp CRM, you first need to connect a WhatsApp number to your account.",
    icon: <Smartphone size={22} />,
    color: "#25D366",
    bgLight: "rgba(37, 211, 102, 0.12)",
    actionRoute: "/dashboard/whatsapp-setup",
    actionLabel: "Setup WhatsApp",
    steps: [
      {
        title: "Click on “Setup WhatsApp”",
        description:
          "From the WhatsApp CRM section, click the “Setup WhatsApp” button to begin the setup process.",
      },
      {
        title: "Choose a WhatsApp number",
        description:
          "You will see two options:\n• Use Registered Studio Number – Select this option if you want to use the WhatsApp number already registered with your studio account.\n• Use a New Mobile Number – Select this option if you want to connect a different/new mobile number for WhatsApp CRM.",
      },
      {
        title: "Click “Submit Details”",
        description:
          "After selecting the required option, click “Submit Details” to submit your WhatsApp setup request.",
      },
      {
        title: "Request Goes for Approval",
        description:
          "Once the details are submitted, your request will be sent for approval. You can start using the WhatsApp CRM after the setup request has been approved.",
      },
    ],
    mockup: {
      header: "WhatsApp Number Setup",
      items: [
        "1. Click 'Setup WhatsApp'",
        "2. Choose Registered or New Number",
        "3. Click 'Submit Details'",
        "4. Request Goes for Approval",
      ],
      highlight: "Start using once request is approved",
    },
  },
  {
    id: 2,
    badgeTitle: "2. Recharge Wallet",
    title: "Recharge Studio Wallet",
    subtitle: "Pre-fund your messaging balance for automated broadcasts and marketing campaigns.",
    icon: <Wallet size={22} />,
    color: "#CE56B0",
    bgLight: "rgba(206, 86, 176, 0.12)",
    actionRoute: "/dashboard/whatsapp/billing",
    actionLabel: "Go to Billing & Wallet",
    steps: [
      {
        title: "Open 'Billing & Usage'",
        description:
          "Click the Wallet Balance card on the WhatsApp dashboard or select Billing & Usage from Quick Actions.",
      },
      {
        title: "Select Recharge Amount",
        description:
          "Choose a quick recharge pack (₹500, ₹1,000, ₹2,000, ₹5,000) or type a custom balance amount.",
      },
      {
        title: "Instant Online Payment",
        description:
          "Pay securely via UPI, Google Pay, PhonePe, Debit/Credit Card, or Netbanking powered by Razorpay.",
      },
      {
        title: "Auto-Deduction per Broadcast",
        description:
          "Funds reflect immediately in your Studio Wallet. When campaigns run, charges are deducted automatically per recipient.",
      },
    ],
    mockup: {
      header: "Studio Owner Wallet",
      items: [
        "💰 Wallet Balance: ₹2,500.00",
        "⚡ Standard Utility Rate: ~₹1.00 / message",
        "💳 Instant 1-click Razorpay / UPI recharge",
        "📊 Detailed ledger & debit history per campaign",
      ],
      highlight: "Auto-Balance Check before each broadcast",
    },
    tip: "You only pay for sent template messages. Inbound replies from clients initiate a 24-hour free service window!",
  },
  {
    id: 3,
    badgeTitle: "3. Create Template",
    title: "Create WhatsApp Templates",
    subtitle: "Design message templates with dynamic personalization variables and buttons.",
    icon: <FileText size={22} />,
    color: "#3B82F6",
    bgLight: "rgba(59, 130, 246, 0.12)",
    actionRoute: "/dashboard/whatsapp/templates?create=true",
    actionLabel: "Create New Template",
    steps: [
      {
        title: "Navigate to 'Templates'",
        description:
          "Open Templates from Quick Actions and click '+ New Template' in the top-right corner.",
      },
      {
        title: "Set Name & Internal Notes",
        description:
          "Choose a lowercase identifier (e.g. 'google_review_offer') and write an internal note for your team.",
      },
      {
        title: "Write Body with {{1}}, {{2}} Variables",
        description:
          "Draft your message and insert numbered parameters like 'Heyy {{1}}, Thanks for choosing {{2}}!'.",
      },
      {
        title: "Add Quick Reply or Website URL Buttons",
        description:
          "Attach interactive buttons such as 'Review on Google' with your direct review link or 'Book Next Session'.",
      },
      {
        title: "Submit for Instant Meta Approval",
        description:
          "Templates are automatically submitted to Meta. Templates are typically approved within 5 to 10 minutes!",
      },
    ],
    mockup: {
      header: "Approved Template Structure",
      items: [
        "📝 Name: diwali_review_invite (EN)",
        "💬 Body: Heyy {{1}}, thank you from {{2}}!",
        "🔗 Button: [★ Review on Google] (URL)",
      ],
      highlight: "Status: APPROVED (Ready for Campaigns)",
    },
   
  },
  {
    id: 4,
    badgeTitle: "4. Campaigns",
    title: "Launch Broadcast Campaigns",
    subtitle: "Send personalized WhatsApp broadcasts to clients with 1-click token mapping and deduplication.",
    icon: <Megaphone size={22} />,
    color: "#CE56B0",
    bgLight: "rgba(206, 86, 176, 0.12)",
    actionRoute: "/dashboard/whatsapp/campaigns?create=true",
    actionLabel: "Create New Campaign",
    steps: [
      {
        title: "Select an Approved Template",
        description:
          "Choose your verified Meta template from the dropdown. You can also create a new one directly from the picker.",
      },
      {
        title: "Choose Target Audience",
        description:
          "Broadcast to 'All Studio Redeem Customers' or filter by specific albums (e.g. Wedding, Reception, Pre-wedding).",
      },
      {
        title: "Automatic Phone Deduplication",
        description:
          "Clients who redeemed multiple album codes will only receive 1 message, saving your wallet credits automatically.",
      },
      {
        title: "1-Click Variable Token Mapping",
        description:
          "Map {{1}} to 'Client Name', {{2}} to 'Studio Name' with 1 click. Check the tall phone preview on the right!",
      },
      {
        title: "Verify Cost & Launch",
        description:
          "Review the wallet balance estimate, recipient count, and click 'Launch Campaign' to start instant broadcast.",
      },
    ],
    mockup: {
      header: "Campaign Broadcast Workflow",
      items: [
        "👥 Audience: Clients (Deduplicated)",
        "📱 Live Phone Preview: Rendered with tokens",
        "💳 Estimated Cost:  (Wallet Sufficient)",
        "🚀 Instant broadcast delivery via Cloud API",
      ],
      highlight: "Real-time delivery & read receipts tracking",
    },
    tip: "Use the live smartphone preview on the right to preview exactly how the client will see their personalized name and studio details.",
  },
  {
    id: 5,
    badgeTitle: "5. Chats",
    title: "Manage Conversations & Replies",
    subtitle: "Respond in real-time to client replies, inquiries, and customer feedback from an integrated inbox.",
    icon: <MessageSquare size={22} />,
    color: "#10B981",
    bgLight: "rgba(16, 185, 129, 0.12)",
    actionRoute: "/dashboard/whatsapp/conversations",
    actionLabel: "Open Conversations Inbox",
    steps: [
      {
        title: "Open 'Conversations'",
        description:
          "Access your unified studio WhatsApp inbox from Quick Actions or the sidebar navigation.",
      },
      {
        title: "Real-Time Incoming Messages",
        description:
          "When a customer replies to your campaign broadcast, their chat immediately lights up with unread indicators.",
      },
      {
        title: "24-Hour Free Service Window",
        description:
          "Every client reply opens a 24-hour customer service window where you can exchange unlimited text messages for FREE!",
      },
      {
        title: "Search or New chat",
        description:
          "Search by client name or phone number, or click new chat to start new conversation",
      },
    ],
    mockup: {
      header: "Two-Way Studio Inbox",
      items: [
        "🟢 Live chat stream with read receipts",
        "⏱️ 24h Customer Service Window: Active (Free)",
        "🔍 Search by Client Name or Number",
      ],
      highlight: "Zero credit charge during 24h reply window",
    },
    tip: "Reply promptly! As long as the customer messages you within 24 hours, you can converse back and forth without using wallet credits.",
  },
];

export const WhatsAppUserGuideModal: React.FC<WhatsAppUserGuideModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const currentStep = GUIDE_STEPS[currentStepIndex];
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === GUIDE_STEPS.length - 1;

  const handleNext = () => {
    if (!isLast) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleActionClick = (route?: string) => {
    if (route) {
      onClose();
      navigate(route);
    }
  };

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <HeaderLeft>
            <HeaderIconWrap>
              <BookOpen size={20} />
            </HeaderIconWrap>
            <div>
              <ModalTitleRow>
                <ModalTitle className="linear-text">WhatsApp CRM User Guide</ModalTitle>
                <StepBadgeTag>
                  Step {currentStepIndex + 1} of {GUIDE_STEPS.length}
                </StepBadgeTag>
              </ModalTitleRow>
              <ModalSubtitle>
                Follow these 5 core steps to master WhatsApp broadcasts and client engagement
              </ModalSubtitle>
            </div>
          </HeaderLeft>

          <CloseBtn onClick={onClose} aria-label="Close user guide">
            <X size={20} />
          </CloseBtn>
        </ModalHeader>

        {/* Stepper Navigation Pills */}
        <StepperBar>
          {GUIDE_STEPS.map((step, idx) => {
            const isActive = idx === currentStepIndex;
            const isDone = idx < currentStepIndex;
            return (
              <StepPillBtn
                key={step.id}
                type="button"
                $active={isActive}
                $done={isDone}
                onClick={() => setCurrentStepIndex(idx)}
              >
                <StepPillNum $active={isActive} $done={isDone}>
                  {isDone ? "✓" : step.id}
                </StepPillNum>
                <StepPillText>{step.badgeTitle}</StepPillText>
              </StepPillBtn>
            );
          })}
        </StepperBar>

        {/* Content Body */}
        <ModalBody>
          {/* Step Banner */}
          <StepBannerWrap $bgLight={currentStep.bgLight} $color={currentStep.color}>
            <StepBannerIcon $color={currentStep.color}>{currentStep.icon}</StepBannerIcon>
            <StepBannerContent>
              <StepBannerTitle>{currentStep.title}</StepBannerTitle>
              <StepBannerSubtitle>{currentStep.subtitle}</StepBannerSubtitle>
            </StepBannerContent>
            {currentStep.actionRoute && (
              <ActionShortcutBtn
                type="button"
                onClick={() => handleActionClick(currentStep.actionRoute)}
              >
                <span>{currentStep.actionLabel}</span>
                <ArrowRight size={14} />
              </ActionShortcutBtn>
            )}
          </StepBannerWrap>

          {/* 2-Column Guide Layout: Steps on Left, Mockup on Right */}
          <GuideContentGrid>
            {/* Left: Actionable Numbered Steps */}
            <StepsCol>
              <StepsList>
                {currentStep.steps.map((s, idx) => (
                  <StepItem key={idx}>
                    <StepItemNumber>{idx + 1}</StepItemNumber>
                    <StepItemBody>
                      <StepItemHeading>{s.title}</StepItemHeading>
                      <StepItemDesc>{s.description}</StepItemDesc>
                    </StepItemBody>
                  </StepItem>
                ))}
              </StepsList>
            </StepsCol>

            {/* Right: Infographic Mockup & Highlights */}
            <MockupCol>
              <MockupCard>
                <MockupCardHeader>
                  <MockupDotRow>
                    <MockupDot color="#ef4444" />
                    <MockupDot color="#f59e0b" />
                    <MockupDot color="#10b981" />
                  </MockupDotRow>
                  <MockupHeaderTitle>{currentStep.mockup.header}</MockupHeaderTitle>
                </MockupCardHeader>

                <MockupList>
                  {currentStep.mockup.items.map((item, i) => (
                    <MockupListItem key={i}>
                      <span>{item}</span>
                    </MockupListItem>
                  ))}
                </MockupList>

                {currentStep.mockup.highlight && (
                  <MockupHighlightBadge>
                    <CheckCircle2 size={14} />
                    <span>{currentStep.mockup.highlight}</span>
                  </MockupHighlightBadge>
                )}
              </MockupCard>

              {/* Tip Callout Box */}
              {currentStep.tip && (
                <TipCard>
                  <TipIconWrap>
                    <Lightbulb size={16} />
                  </TipIconWrap>
                  <div>
                    <TipTitle>Important Tip:</TipTitle>
                    <TipText>{currentStep.tip}</TipText>
                  </div>
                </TipCard>
              )}
            </MockupCol>
          </GuideContentGrid>
        </ModalBody>

        {/* Footer Navigation */}
        <ModalFooter>
          <PrevBtn type="button" onClick={handlePrev} disabled={isFirst}>
            <ChevronLeft size={18} />
            <span>Previous</span>
          </PrevBtn>

          {/* Dots Indicator */}
          <DotsGroup>
            {GUIDE_STEPS.map((_, i) => (
              <DotIndicator
                key={i}
                $active={i === currentStepIndex}
                onClick={() => setCurrentStepIndex(i)}
                title={`Go to step ${i + 1}`}
              />
            ))}
          </DotsGroup>

          <NextBtn type="button" onClick={handleNext} $isLast={isLast}>
            <span>{isLast ? "Finish Guide" : "Next Step"}</span>
            {isLast ? <CheckCircle2 size={18} /> : <ChevronRight size={18} />}
          </NextBtn>
        </ModalFooter>
      </ModalContainer>
    </ModalBackdrop>
  );
};

// ── Styled Components ────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.65);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(23, 23, 23, 0.08);
  overflow: hidden;
  animation: ${slideUp} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ModalHeader = styled.div`
  padding: 16px 24px 14px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.07);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: #fafafa;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeaderIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.22) 0%, rgba(206, 86, 176, 0.16) 100%);
  color: #ce56b0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(206, 86, 176, 0.25);
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const StepBadgeTag = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.18) 0%, rgba(206, 86, 176, 0.14) 100%);
  border: 1px solid rgba(206, 86, 176, 0.25);
  color: #ce56b0;
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 700;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 12.5px;
  color: rgba(23, 23, 23, 0.6);
  line-height: 1.35;
`;

const CloseBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
    color: #111827;
    transform: scale(1.05);
  }
`;

const StepperBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  background: #fff;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;

const StepPillBtn = styled.button<{ $active: boolean; $done: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 10px;
  background: ${(p) => (p.$active ? "#171717" : p.$done ? "#f3f4f6" : "transparent")};
  color: ${(p) => (p.$active ? "#ffffff" : p.$done ? "#374151" : "#9ca3af")};
  border: 1px solid ${(p) => (p.$active ? "#171717" : "transparent")};
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: ${(p) => (p.$active ? "#171717" : "#f9fafb")};
    color: ${(p) => (p.$active ? "#ffffff" : "#111827")};
  }
`;

const StepPillNum = styled.span<{ $active: boolean; $done: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, #ffc951 0%, #ce56b0 100%)"
      : p.$done
      ? "#10b981"
      : "#e5e7eb"};
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepPillText = styled.span`
  line-height: 1;
`;

const ModalBody = styled.div`
  padding: 18px 26px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;

const StepBannerWrap = styled.div<{ $bgLight: string; $color: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-radius: 14px;
  background: ${(p) => p.$bgLight};
  border: 1px solid ${(p) => p.$color}30;
  flex-wrap: wrap;
`;

const StepBannerIcon = styled.div<{ $color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #ffffff;
  color: ${(p) => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
`;

const StepBannerContent = styled.div`
  flex: 1;
  min-width: 220px;
`;

const StepBannerTitle = styled.h3`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #171717;
`;

const StepBannerSubtitle = styled.p`
  margin: 3px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.65);
`;

const ActionShortcutBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: linear-gradient(
      144.01deg,
      rgba(255, 201, 81, 0.95) 0%,
      rgba(206, 86, 176, 0.95) 100%
    );
    color: #ffffff;
    border-color: transparent;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(206, 86, 176, 0.25);
  }
`;

const GuideContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 22px;
  align-items: flex-start;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const StepsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const StepItemNumber = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(144.01deg, #ffc951 0%, #ce56b0 100%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  box-shadow: 0 2px 8px rgba(206, 86, 176, 0.25);
`;

const StepItemBody = styled.div`
  flex: 1;
`;

const StepItemHeading = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
  line-height: 1.25;
`;

const StepItemDesc = styled.div`
  margin-top: 3px;
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.45;
  white-space: pre-line;
`;

const MockupCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MockupCard = styled.div`
  background: #fdfdfd;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
`;

const MockupCardHeader = styled.div`
  background: #f3f4f6;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.08);
`;

const MockupDotRow = styled.div`
  display: flex;
  gap: 5px;
`;

const MockupDot = styled.div<{ color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.color};
`;

const MockupHeaderTitle = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #4b5563;
`;

const MockupList = styled.div`
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MockupListItem = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
`;

const MockupHighlightBadge = styled.div`
  margin: 0 16px 14px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TipCard = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const TipIconWrap = styled.div`
  color: #d97706;
  flex-shrink: 0;
  margin-top: 1px;
`;

const TipTitle = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 800;
  color: #92400e;
`;

const TipText = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11.5px;
  color: #78350f;
  margin-top: 2px;
  line-height: 1.4;
`;

const ModalFooter = styled.div`
  padding: 12px 24px;
  border-top: 1px solid rgba(23, 23, 23, 0.07);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fafafa;
  gap: 16px;
`;

const PrevBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 12px;
  background: #ffffff;
  color: #374151;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const NextBtn = styled.button<{ $isLast?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 12px;
  background: ${(p) =>
    p.$isLast
      ? "#10b981"
      : "linear-gradient(144.01deg, rgba(255, 201, 81, 0.95) 0%, rgba(206, 86, 176, 0.95) 100%)"};
  color: #ffffff;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 800;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 4px 14px
    ${(p) =>
      p.$isLast
        ? "rgba(16, 185, 129, 0.25)"
        : "rgba(206, 86, 176, 0.28)"};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px
      ${(p) =>
        p.$isLast
          ? "rgba(16, 185, 129, 0.35)"
          : "rgba(206, 86, 176, 0.38)"};
  }

  &:active {
    transform: translateY(0);
  }
`;

const DotsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DotIndicator = styled.button<{ $active: boolean }>`
  width: ${(p) => (p.$active ? "24px" : "8px")};
  height: 8px;
  border-radius: 4px;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, #ffc951 0%, #ce56b0 100%)"
      : "#d1d5db"};
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: ${(p) =>
      p.$active
        ? "linear-gradient(144.01deg, #ffc951 0%, #ce56b0 100%)"
        : "#9ca3af"};
  }
`;
